import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '../config';
import { Theme } from '../ui/theme';
import { GameState } from '../systems/GameState';
import { OutfitRenderer } from '../systems/OutfitRenderer';
import { OutfitIconRenderer } from '../systems/OutfitIconRenderer';
import { OUTFITS_BY_ID, OUTFITS_BY_SLOT } from '../data/outfits';
import { svgStringToTexture } from '../utils/svgToTexture';
import { Button } from '../ui/Button';
import { EventBus } from '../systems/EventBus';
import { ToastManager } from '../ui/ToastManager';
import { AudioManager } from '../systems/AudioManager';
import { UnlockManager } from '../systems/UnlockManager';
import { QuestEngine } from '../systems/QuestEngine';
import type { OutfitSlot, HairStyle } from '../types';

const SLOTS: { id: OutfitSlot; label: string; emoji: string }[] = [
  { id: 'top',     label: '상의',   emoji: '👕' },
  { id: 'bottom',  label: '하의',   emoji: '👖' },
  { id: 'hair',    label: '헤어',   emoji: '💇' },
  { id: 'hairpin', label: '머리삔', emoji: '🎀' },
  { id: 'glasses', label: '안경',   emoji: '🕶️' },
  { id: 'bag',     label: '가방',   emoji: '🛍️' },
  { id: 'shoes',   label: '신발',   emoji: '👟' },
];

const RARITY_COLOR: Record<string, number> = {
  common: 0xcccccc,
  rare:   0x6fd4c4,
  epic:   0x9b6bff,
  legend: 0xffc94c,
  season: 0xff6fa1,
};

// Grid layout constants
const GRID_START_X = GAME_WIDTH / 2 + 100;
const GRID_COLS = 3;
const CELL_SIZE = 130;

// Scrollable area bounds
const GRID_TOP = 195;
const GRID_BOTTOM = GAME_HEIGHT - 90;
const VISIBLE_HEIGHT = GRID_BOTTOM - GRID_TOP;

const RARITY_ORDER: Record<string, number> = { legend: 0, epic: 1, season: 2, rare: 3, common: 4 };

export class ClosetScene extends Phaser.Scene {
  private currentSlot: OutfitSlot = 'hair';
  private characterSprite: Phaser.GameObjects.Image | null = null;
  private previewSprite: Phaser.GameObjects.Image | null = null;
  private previewLabel: Phaser.GameObjects.Text | null = null;
  private gridContainer: Phaser.GameObjects.Container | null = null;
  private tabContainer: Phaser.GameObjects.Container | null = null;
  private filterContainer: Phaser.GameObjects.Container | null = null;
  private showOwnedOnly = true;
  private toast!: ToastManager;

  // Scroll state — target/current 분리해서 부드럽게 lerp
  private scrollY = 0;
  private targetScrollY = 0;
  private maxScrollY = 0;
  private isDragging = false;
  private dragStartY = 0;
  private dragStartScroll = 0;
  private dragLastY = 0;
  private dragLastTime = 0;
  private dragVelocity = 0;
  private scrollbarTrack: Phaser.GameObjects.Rectangle | null = null;
  private scrollbarThumb: Phaser.GameObjects.Rectangle | null = null;
  private gridMask: Phaser.Display.Masks.GeometryMask | null = null;

  constructor() { super('ClosetScene'); }

  async create(): Promise<void> {
    AudioManager.setScene(this);
    AudioManager.playBgm('closet');
    this.scene.launch('HUDScene');
    this.cameras.main.setBackgroundColor('#ffeef5');
    if (this.textures.exists('bg_closet')) {
      const bg = this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, 'bg_closet');
      // 화면을 채우도록 스케일 — cover 방식
      const sx = GAME_WIDTH / bg.width;
      const sy = GAME_HEIGHT / bg.height;
      const s = Math.max(sx, sy);
      bg.setScale(s).setDepth(-100);
      // 가독성용 살짝 톤 다운
      this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0xffeef5, 0.55).setDepth(-99);
    }
    this.cameras.main.fadeIn(200);
    this.toast = new ToastManager(this);
    this.scrollY = 0;
    this.targetScrollY = 0;

    // Title
    this.add.text(GAME_WIDTH / 2, 72, '👗 옷장', {
      fontFamily: Theme.font.family,
      fontSize: '34px',
      fontStyle: '800',
      color: Theme.hex.text,
    }).setOrigin(0.5);

    // Left: character preview
    await this.refreshCharacter();
    this._buildPreviewPanel();

    // Right: tabs + grid
    this.renderTabs();
    this.renderFilters();
    await this.renderOutfitGrid();

    // Bottom buttons
    new Button(this, 100, GAME_HEIGHT - 50, '← 홈', () => {
      this.cameras.main.fadeOut(200);
      this.cameras.main.once('camerafadeoutcomplete', () => {
        this.scene.stop('HUDScene');
        this.scene.start('HomeScene');
      });
    }, { width: 120, height: 48, style: 'secondary' });

    new Button(this, GAME_WIDTH - 240, GAME_HEIGHT - 50, '전체 벗기', () => this.resetAll(), { width: 140, height: 48, style: 'secondary' });
    new Button(this, GAME_WIDTH - 80, GAME_HEIGHT - 50, '🎲 랜덤', () => this.randomOutfit(), { width: 120, height: 48, style: 'gold' });

    this._setupScrollInput();
  }

  // ── Preview panel (small character in bottom-left corner) ─────────────────

  private _buildPreviewPanel(): void {
    // Label
    this.previewLabel = this.add.text(360, GAME_HEIGHT - 170, '', {
      fontFamily: 'Pretendard Variable, Pretendard, sans-serif',
      fontSize: '11px',
      fontStyle: '700',
      color: '#9b6bff',
      align: 'center',
    }).setOrigin(0.5).setDepth(10);
  }

  // 미리보기 디바운스/cancel 관리
  private _previewDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private _previewGen = 0; // 인플라이트 식별자

  private _queuePreview(outfitId: string): void {
    // 직전 디바운스 타이머 취소
    if (this._previewDebounceTimer !== null) {
      clearTimeout(this._previewDebounceTimer);
    }
    // 150ms 안 머무르면 안 그림 → 빠른 hover 시 텍스처 폭주 방지
    this._previewDebounceTimer = setTimeout(() => {
      this._previewDebounceTimer = null;
      void this._showPreview(outfitId);
    }, 150);
  }

  private async _showPreview(outfitId: string): Promise<void> {
    const outfit = OUTFITS_BY_ID[outfitId];
    if (!outfit) return;

    // 빠른 경로 — 빡빡이 + 헤어 오버레이 합성 (헤어 슬롯에서 가장 효과적)
    const trialEquipped = { ...GameState.data.currentEquipped, [outfit.slot]: outfitId };
    const equipped = OutfitRenderer.resolveEquipped(trialEquipped, OUTFITS_BY_ID);
    const hairId = equipped.hair?.id;

    if (!this.textures.exists('character_bald')) return;

    if (this.previewSprite) this.previewSprite.destroy();
    const cx = 360, cy = GAME_HEIGHT / 2 + 20;
    const fullKey = hairId ? `char_full_${hairId}` : null;
    const useKey = fullKey && this.textures.exists(fullKey) ? fullKey : 'character_bald';
    this.previewSprite = this.add.image(cx, cy, useKey).setScale(2.25).setDepth(11);

    if (this.characterSprite) this.characterSprite.setVisible(false);

    if (this.previewLabel) {
      this.previewLabel.setText(`👁 미리보기: ${outfit.name}`).setDepth(12);
    }
  }

  private _clearPreview(): void {
    if (this._previewDebounceTimer !== null) {
      clearTimeout(this._previewDebounceTimer);
      this._previewDebounceTimer = null;
    }
    this._previewGen++;
    if (this.previewSprite) {
      this.previewSprite.destroy();
      this.previewSprite = null;
    }
    if (this.previewLabel) this.previewLabel.setText('');
    // 본 캐릭터 다시 보이게
    if (this.characterSprite) this.characterSprite.setVisible(true);
  }

  // ── Scroll setup ───────────────────────────────────────────────────────────

  private _setupScrollInput(): void {
    // Mouse wheel — target에 누적, update에서 lerp로 부드럽게 따라감
    this.input.on('wheel', (_ptr: Phaser.Input.Pointer, _gos: unknown, _dx: number, dy: number) => {
      this.targetScrollY = Phaser.Math.Clamp(this.targetScrollY + dy * 0.6, 0, Math.max(0, this.maxScrollY));
    });

    // Touch drag — only in the grid area (right half)
    this.input.on('pointerdown', (p: Phaser.Input.Pointer) => {
      if (p.x > GAME_WIDTH / 2) {
        this.isDragging = true;
        this.dragStartY = p.y;
        this.dragStartScroll = this.scrollY;
        this.dragLastY = p.y;
        this.dragLastTime = performance.now();
        this.dragVelocity = 0;
        // 드래그 중에는 즉시 위치 = target
        this.targetScrollY = this.scrollY;
      }
    });

    this.input.on('pointermove', (p: Phaser.Input.Pointer) => {
      if (!this.isDragging) return;
      const now = performance.now();
      const dt = Math.max(1, now - this.dragLastTime);
      this.dragVelocity = (this.dragLastY - p.y) / dt * 16; // px per ~frame
      this.dragLastY = p.y;
      this.dragLastTime = now;
      const delta = this.dragStartY - p.y;
      const newY = Phaser.Math.Clamp(this.dragStartScroll + delta, 0, Math.max(0, this.maxScrollY));
      this.scrollY = newY;
      this.targetScrollY = newY;
      if (this.gridContainer) this.gridContainer.y = GRID_TOP - this.scrollY;
      this._updateScrollbar();
    });

    const endDrag = () => {
      if (!this.isDragging) return;
      this.isDragging = false;
      // 관성 — 드래그 종료 시 속도를 target에 더해서 미끄러지듯 멈춤
      this.targetScrollY = Phaser.Math.Clamp(this.scrollY + this.dragVelocity * 12, 0, Math.max(0, this.maxScrollY));
    };
    this.input.on('pointerup', endDrag);
    this.input.on('pointerupoutside', endDrag);
  }

  update(_time: number, delta: number): void {
    // 부드럽게 target까지 lerp
    if (!this.isDragging && Math.abs(this.targetScrollY - this.scrollY) > 0.1) {
      const t = Math.min(1, (delta / 16) * 0.22);
      this.scrollY += (this.targetScrollY - this.scrollY) * t;
      if (this.gridContainer) this.gridContainer.y = GRID_TOP - this.scrollY;
      this._updateScrollbar();
    }
  }

  private _updateScrollbar(): void {
    if (!this.scrollbarThumb || !this.scrollbarTrack) return;
    if (this.maxScrollY <= 0) {
      this.scrollbarThumb.setVisible(false);
      return;
    }
    this.scrollbarThumb.setVisible(true);
    const trackH = this.scrollbarTrack.height;
    const thumbH = Math.max(40, (VISIBLE_HEIGHT / (VISIBLE_HEIGHT + this.maxScrollY)) * trackH);
    const thumbY = this.scrollbarTrack.y - trackH / 2 + (this.scrollY / this.maxScrollY) * (trackH - thumbH) + thumbH / 2;
    this.scrollbarThumb.setDisplaySize(8, thumbH);
    this.scrollbarThumb.setPosition(this.scrollbarTrack.x, thumbY);
  }

  // ── Tabs ─────────────────────────────────────────────────────────────────

  private renderTabs(): void {
    if (this.tabContainer) this.tabContainer.destroy();
    this.tabContainer = this.add.container(0, 0);
    const startX = GAME_WIDTH / 2 + 40;
    const y = 110;
    // 아이템이 있는 슬롯만 탭으로 표시 (클린 슬레이트 단계 — 헤어만 있음)
    const visibleSlots = SLOTS.filter(s => (OUTFITS_BY_SLOT[s.id]?.length ?? 0) > 0);
    visibleSlots.forEach((slot, i) => {
      const x = startX + (i % 4) * 130;
      const yy = y + Math.floor(i / 4) * 60;
      const isActive = slot.id === this.currentSlot;

      const g = this.add.graphics();
      const bgColor = isActive ? 0xff6fa1 : 0xfff5f8;
      g.fillStyle(bgColor, 1);
      g.fillRoundedRect(x - 58, yy - 22, 116, 44, 12);
      if (!isActive) {
        g.lineStyle(1.5, 0xffd6e6, 1);
        g.strokeRoundedRect(x - 58, yy - 22, 116, 44, 12);
      }

      const hit = this.add.rectangle(x, yy, 116, 44, 0x000000, 0);
      hit.setInteractive({ useHandCursor: true });
      hit.on('pointerdown', () => {
        this.currentSlot = slot.id;
        this.scrollY = 0;
    this.targetScrollY = 0;
        this.renderTabs();
        void this.renderOutfitGrid();
      });

      const label = this.add.text(x, yy, `${slot.emoji} ${slot.label}`, {
        fontFamily: 'Pretendard Variable, Pretendard, sans-serif',
        fontSize: '14px',
        fontStyle: '700',
        color: isActive ? '#ffffff' : '#7a5b80',
      }).setOrigin(0.5);
      this.tabContainer!.add([g, hit, label]);
    });
  }

  // ── Filters (owned checkbox) ──────────────────────────────────────────────

  private renderFilters(): void {
    if (this.filterContainer) this.filterContainer.destroy();
    this.filterContainer = this.add.container(0, 0);
    const cx = GAME_WIDTH / 2 + 60;
    const cy = 168;

    // 체크박스 박스
    const boxSize = 18;
    const box = this.add.graphics();
    box.fillStyle(this.showOwnedOnly ? 0xff6fa1 : 0xffffff, 1);
    box.fillRoundedRect(cx - boxSize / 2, cy - boxSize / 2, boxSize, boxSize, 4);
    box.lineStyle(2, 0xff6fa1, 1);
    box.strokeRoundedRect(cx - boxSize / 2, cy - boxSize / 2, boxSize, boxSize, 4);
    this.filterContainer.add(box);

    if (this.showOwnedOnly) {
      const tick = this.add.text(cx, cy, '✓', {
        fontFamily: 'Pretendard Variable, Pretendard, sans-serif',
        fontSize: '14px', fontStyle: '900', color: '#ffffff',
      }).setOrigin(0.5);
      this.filterContainer.add(tick);
    }

    const label = this.add.text(cx + 14, cy, '보유중만 보기', {
      fontFamily: 'Pretendard Variable, Pretendard, sans-serif',
      fontSize: '13px', fontStyle: '700', color: '#7a5b80',
    }).setOrigin(0, 0.5);
    this.filterContainer.add(label);

    const hit = this.add.rectangle(cx + 60, cy, 160, 28, 0x000000, 0);
    hit.setInteractive({ useHandCursor: true });
    hit.on('pointerdown', () => {
      this.showOwnedOnly = !this.showOwnedOnly;
      this.scrollY = 0;
      this.targetScrollY = 0;
      this.renderFilters();
      void this.renderOutfitGrid();
    });
    this.filterContainer.add(hit);

    // 정렬 표시 (단순 라벨)
    const sortLabel = this.add.text(cx + 200, cy, '⭐ 등급순', {
      fontFamily: 'Pretendard Variable, Pretendard, sans-serif',
      fontSize: '12px', fontStyle: '700', color: '#9b6bff',
    }).setOrigin(0, 0.5);
    this.filterContainer.add(sortLabel);
  }

  // ── Grid ──────────────────────────────────────────────────────────────────

  private async renderOutfitGrid(): Promise<void> {
    if (this.gridContainer) this.gridContainer.destroy();
    if (this.scrollbarTrack) this.scrollbarTrack.destroy();
    if (this.scrollbarThumb) this.scrollbarThumb.destroy();

    const allItems = OUTFITS_BY_SLOT[this.currentSlot] ?? [];
    const ownedIds = new Set(GameState.data.ownedOutfits.map(o => o.outfitId));
    const filtered = this.showOwnedOnly ? allItems.filter(it => ownedIds.has(it.id)) : allItems;
    const items = [...filtered].sort((a, b) => {
      const ra = RARITY_ORDER[a.rarity] ?? 99;
      const rb = RARITY_ORDER[b.rarity] ?? 99;
      if (ra !== rb) return ra - rb;
      return a.name.localeCompare(b.name);
    });
    const equippedId = GameState.data.currentEquipped[this.currentSlot];

    // Container positioned at GRID_TOP initially
    this.gridContainer = this.add.container(0, GRID_TOP);

    // Calculate content height
    const rows = Math.ceil(items.length / GRID_COLS);
    // 첫 셀 상단(y=5)부터 마지막 셀 하단까지 커버 + 여유
    const contentHeight = (rows - 1) * CELL_SIZE + 120 + 40; // 셀 110 height + label 아래 여유
    this.maxScrollY = Math.max(0, contentHeight - VISIBLE_HEIGHT);

    // Mask so grid is clipped to visible area
    const maskShape = this.make.graphics({});
    maskShape.fillStyle(0xffffff);
    maskShape.fillRect(GAME_WIDTH / 2 + 10, GRID_TOP, GAME_WIDTH / 2 - 10, VISIBLE_HEIGHT);
    this.gridMask = maskShape.createGeometryMask();
    this.gridContainer.setMask(this.gridMask);

    const promises: Promise<void>[] = [];

    // Relative Y positions inside container (relative to GRID_TOP)
    const relStartX = GRID_START_X;
    // 첫 아이템 셀 중심을 y=60에 두면 상단(y=5)이 마스크 안 (GRID_TOP=195 + 5 = 200)에 들어감.
    // 옛 30 → 25px 짤림 버그 수정.
    const itemOffsetY = 60;

    items.forEach((it, i) => {
      const x = relStartX + (i % GRID_COLS) * CELL_SIZE;
      const y = itemOffsetY + Math.floor(i / GRID_COLS) * CELL_SIZE;
      const owned = ownedIds.has(it.id);
      const equipped = it.id === equippedId;
      const rarityCol = RARITY_COLOR[it.rarity] ?? 0xcccccc;

      // Drop shadow
      const shadow = this.add.graphics();
      shadow.fillStyle(0x3a2540, 0.1);
      shadow.fillRoundedRect(x - 52, y - 52 + 4, 110, 110, 14);

      // Cell background
      const bg = this.add.graphics();
      bg.fillStyle(owned ? 0xffffff : 0xeeeeee, 1);
      bg.fillRoundedRect(x - 55, y - 55, 110, 110, 14);
      if (equipped) {
        bg.lineStyle(3, 0xffc94c, 1);
      } else {
        bg.lineStyle(2, rarityCol, owned ? 0.9 : 0.4);
      }
      bg.strokeRoundedRect(x - 55, y - 55, 110, 110, 14);

      if (it.rarity === 'legend' && owned) {
        this.tweens.add({ targets: bg, alpha: 0.6, duration: 1200, yoyo: true, repeat: -1, ease: 'Sine.InOut' });
      }

      const label = this.add.text(x, y - 42, it.name, {
        fontFamily: 'Pretendard Variable, Pretendard, sans-serif',
        fontSize: '10px',
        fontStyle: '700',
        color: owned ? '#3a2540' : '#888888',
        align: 'center',
        wordWrap: { width: 100 },
      }).setOrigin(0.5);

      if (equipped) {
        const badge = this.add.graphics();
        badge.fillStyle(0xffc94c, 1);
        badge.fillCircle(x + 41, y - 41, 10);
        const tick = this.add.text(x + 41, y - 41, '✓', { fontSize: '11px', fontStyle: '900', color: '#ffffff' }).setOrigin(0.5);
        this.gridContainer!.add([badge, tick]);
      }

      // Rarity badge (top-right corner)
      const rarityNames: Record<string, string> = {
        common: 'C', rare: 'R', epic: 'E', legend: 'L', season: 'S',
      };
      const rarityBg = this.add.graphics();
      rarityBg.fillStyle(rarityCol, 1);
      rarityBg.fillRoundedRect(x + 26, y - 55, 28, 18, 5);
      const rarityText = this.add.text(x + 40, y - 46, rarityNames[it.rarity] ?? 'C', {
        fontFamily: 'Pretendard Variable, Pretendard, sans-serif',
        fontSize: '10px',
        fontStyle: '900',
        color: it.rarity === 'common' ? '#555555' : '#ffffff',
      }).setOrigin(0.5);
      this.gridContainer!.add([rarityBg, rarityText]);

      if (!owned) {
        // Lock icon + unlock condition text
        const lockIcon = this.add.text(x, y + 10, '🔒', { fontSize: '18px' }).setOrigin(0.5);
        const lockLabel = UnlockManager.getLockLabel(it);
        const lockText = this.add.text(x, y + 34, lockLabel, {
          fontFamily: 'Pretendard Variable, Pretendard, sans-serif',
          fontSize: '8px',
          fontStyle: '700',
          color: '#cc4466',
          align: 'center',
          wordWrap: { width: 100 },
        }).setOrigin(0.5);
        this.gridContainer!.add([lockIcon, lockText]);
      }

      this.gridContainer!.add([shadow, bg, label]);

      // Outfit icon — 캐시된 텍스처가 있으면 SVG 재생성 스킵
      // 헤어 슬롯: 풀캐릭터 PNG 자체를 썸네일로 사용 (실제와 100% 일치)
      const fullKey = it.slot === 'hair' ? `char_full_${it.id}` : null;
      if (fullKey && this.textures.exists(fullKey)) {
        const icon = this.add.image(x, y + 14, fullKey);
        icon.setScale(0.27); // 192x288 → 약 52x78px (라벨/뱃지 가리지 않음)
        if (!owned) icon.setAlpha(0.35);
        this.gridContainer!.add(icon);
      } else {
        const hairStyle = (it.slot === 'hair' && it.hairStyle) ? it.hairStyle as HairStyle : undefined;
        const iconKey = `icon_${it.id}`;
        if (this.textures.exists(iconKey)) {
          const icon = this.add.image(x, y + 6, iconKey);
          icon.setScale(2.2);
          if (!owned) icon.setAlpha(0.35);
          this.gridContainer!.add(icon);
        } else {
          const svgStr = OutfitIconRenderer.render(it.slot, it.color, hairStyle, it.iconStyle);
          const p = svgStringToTexture(this, iconKey, svgStr).then(() => {
            if (!this.scene.isActive()) return;
            const icon = this.add.image(x, y + 6, iconKey);
            icon.setScale(2.2);
            if (!owned) icon.setAlpha(0.35);
            this.gridContainer!.add(icon);
          });
          promises.push(p);
        }
      }

      // Hit zone — hover preview + click equip
      const hit = this.add.rectangle(x, y, 110, 110, 0x000000, 0);
      hit.setInteractive({ useHandCursor: owned });

      if (owned) {
        // 디바운스된 큐 — 빠른 hover 시 한꺼번에 텍스처 만들지 않음
        hit.on('pointerover', () => this._queuePreview(it.id));
        hit.on('pointerout', () => this._clearPreview());
        // (pointerout 시 refreshCharacter 호출 안 함 — 본 캐릭터는 변동 없음)
      }

      hit.on('pointerdown', () => {
        if (!owned) {
          // 옷장에서는 해금 시도하지 않음 — 상점에서만 구매 가능
          this.toast.show('🛒 상점에서 먼저 구매해주세요!', 'error');
          return;
        }
        void this.equipOutfit(this.currentSlot, it.id);
      });
      this.gridContainer!.add(hit);
    });

    // Scrollbar
    const sbX = GAME_WIDTH - 20;
    this.scrollbarTrack = this.add.rectangle(sbX, GRID_TOP + VISIBLE_HEIGHT / 2, 8, VISIBLE_HEIGHT, 0xddd0e6, 0.5);
    this.scrollbarThumb = this.add.rectangle(sbX, GRID_TOP + 20, 8, 40, 0xaa88cc);
    this._updateScrollbar();

    // Apply current scroll offset
    this.gridContainer.y = GRID_TOP - this.scrollY;

    await Promise.allSettled(promises);
  }

  // ── Actions ───────────────────────────────────────────────────────────────

  private async equipOutfit(slot: OutfitSlot, outfitId: string): Promise<void> {
    GameState.data.currentEquipped[slot] = outfitId;
    // 의상 입어보기 카운터 + 퀘스트 진행도 갱신
    GameState.data.stats.outfitEquipCount = (GameState.data.stats.outfitEquipCount ?? 0) + 1;
    QuestEngine.recordEvent('outfit_equips', GameState.data.stats.outfitEquipCount);
    QuestEngine.recordEvent('outfits_owned', GameState.data.ownedOutfits.length);
    GameState.persist();
    EventBus.emit('outfit-equipped', { slot, outfitId });
    AudioManager.playSfx('equip');
    this.toast.show(`✨ ${OUTFITS_BY_ID[outfitId].name}`);
    this._clearPreview();
    await this.refreshCharacter();
    this._spawnEquipSparkle();
    // 그리드 전체 재렌더는 무거움 — 대신 "✓" 배지만 갱신
    this._updateEquippedBadges();
  }

  /** 그리드 안 ✓ 배지를 현재 equipped 상태로 갱신 (전체 재렌더 없이) */
  private _updateEquippedBadges(): void {
    // 기존 ✓ 배지 모두 제거 후 현재 equipped만 다시 표시
    if (!this.gridContainer) return;
    // 마커 키로 식별 — equipped 배지 GameObject들은 data 'equipped-badge' = true
    this.gridContainer.list.slice().forEach(go => {
      if ((go as Phaser.GameObjects.GameObject).getData?.('equipped-badge')) {
        go.destroy();
      }
    });
    const equippedId = GameState.data.currentEquipped[this.currentSlot];
    if (!equippedId) return;
    const ownedIds = new Set(GameState.data.ownedOutfits.map(o => o.outfitId));
    const allItems = OUTFITS_BY_SLOT[this.currentSlot] ?? [];
    const filtered = this.showOwnedOnly ? allItems.filter(it => ownedIds.has(it.id)) : allItems;
    const items = [...filtered].sort((a, b) => {
      const ra = RARITY_ORDER[a.rarity] ?? 99;
      const rb = RARITY_ORDER[b.rarity] ?? 99;
      if (ra !== rb) return ra - rb;
      return a.name.localeCompare(b.name);
    });
    const idx = items.findIndex(it => it.id === equippedId);
    if (idx < 0) return;
    const x = GRID_START_X + (idx % GRID_COLS) * CELL_SIZE;
    const y = 30 + Math.floor(idx / GRID_COLS) * CELL_SIZE;
    const badge = this.add.graphics();
    badge.fillStyle(0xffc94c, 1);
    badge.fillCircle(x + 41, y - 41, 10);
    badge.setData('equipped-badge', true);
    const tick = this.add.text(x + 41, y - 41, '✓', { fontSize: '11px', fontStyle: '900', color: '#ffffff' }).setOrigin(0.5);
    tick.setData('equipped-badge', true);
    this.gridContainer.add([badge, tick]);
  }

  private _spawnEquipSparkle(): void {
    const cx = 360;
    const cy = GAME_HEIGHT / 2 + 20;
    const colors = [0xffd95c, 0xff6fa1, 0x9b6bff, 0x6fd4c4, 0xffffff];
    for (let i = 0; i < 10; i++) {
      const angle = (i / 10) * Math.PI * 2;
      const r = this.add.text(cx, cy, '✨', { fontSize: '20px' }).setOrigin(0.5).setDepth(500);
      this.tweens.add({
        targets: r,
        x: cx + Math.cos(angle) * (80 + Math.random() * 40),
        y: cy + Math.sin(angle) * (80 + Math.random() * 40),
        alpha: 0,
        scale: 0.5,
        duration: 800 + Math.random() * 400,
        delay: Math.random() * 200,
        ease: 'Sine.Out',
        onComplete: () => r.destroy(),
      });
      void colors;
    }
  }

  private async resetAll(): Promise<void> {
    GameState.data.currentEquipped = {};
    GameState.persist();
    EventBus.emit('outfit-equipped', {});
    this.toast.show('전체 벗기 완료');
    this._clearPreview();
    await this.refreshCharacter();
    this._updateEquippedBadges();
  }

  private async randomOutfit(): Promise<void> {
    const ownedIds = new Set(GameState.data.ownedOutfits.map(o => o.outfitId));
    for (const slot of SLOTS) {
      const candidates = (OUTFITS_BY_SLOT[slot.id] ?? []).filter(o => ownedIds.has(o.id));
      if (candidates.length > 0) {
        GameState.data.currentEquipped[slot.id] = candidates[Math.floor(Math.random() * candidates.length)].id;
      }
    }
    GameState.persist();
    EventBus.emit('outfit-equipped', {});
    this.toast.show('🎲 랜덤 코디!', 'gold');
    this._clearPreview();
    await this.refreshCharacter();
    this._updateEquippedBadges();
  }

  async refreshCharacter(): Promise<void> {
    const equipped = OutfitRenderer.resolveEquipped(GameState.data.currentEquipped, OUTFITS_BY_ID);
    if (this.characterSprite) this.characterSprite.destroy();

    const cx = 360, cy = GAME_HEIGHT / 2 + 20;
    const hairId = equipped.hair?.id;
    const fullKey = hairId ? `char_full_${hairId}` : null;

    if (fullKey && this.textures.exists(fullKey)) {
      this.characterSprite = this.add.image(cx, cy, fullKey).setScale(2.25);
    } else if (this.textures.exists('character_bald')) {
      this.characterSprite = this.add.image(cx, cy, 'character_bald');
      this.characterSprite.setScale(2.25);
    } else {
      const svg = OutfitRenderer.renderCharacterSVG(equipped);
      const k = 'closet_character_main';
      await svgStringToTexture(this, k, svg);
      this.characterSprite = this.add.image(cx, cy, k);
      this.characterSprite.setScale(2.25);
    }
  }
}
